import type { Estudio, Experiencia, Persona } from "@/componentes/personas/persona"

/**
 * Lo que devuelve `POST /personas/lector-ia` y cómo se convierte en campos de la ficha.
 *
 * ⚠ **La lectura PROPONE; lo que se guarda es lo que queda en el formulario.** Cada
 * campo leído se ofrece con su casilla, marcada sólo si trae algo y es distinto de lo
 * que ya dice la ficha: así «Usar estos datos» no pisa a ciegas un teléfono que alguien
 * corrigió a mano ayer.
 *
 * ⚠ **Los archivos NO vuelven al navegador**: se quedan en el servidor y viajan al
 * guardar con `lectura_id`.
 */

export type TipoArchivo =
  | "CEDULA"
  | "RIF"
  | "CURRICULO"
  | "OTRO"
  // (23/10) Los del checklist del expediente laboral.
  | "TITULO"
  | "CONSTANCIA_TRABAJO"
  | "REFERENCIA_PERSONAL"
  | "CERTIFICADO_MEDICO"
  | "CARGA_FAMILIAR"
  | "CUENTA_BANCARIA"
  | "CONTRATO_FIRMADO"
  | "INSCRIPCION_IVSS"
  | "INSCRIPCION_FAOV"
  | "NOTIFICACION_RIESGOS"

export const NOMBRE_ARCHIVO: Record<TipoArchivo, string> = {
  CEDULA: "Cédula",
  RIF: "RIF",
  CURRICULO: "Síntesis curricular",
  OTRO: "Otro documento",
  TITULO: "Título o certificado",
  CONSTANCIA_TRABAJO: "Constancia de trabajo",
  REFERENCIA_PERSONAL: "Referencia personal",
  CERTIFICADO_MEDICO: "Certificado médico",
  CARGA_FAMILIAR: "Carga familiar",
  CUENTA_BANCARIA: "Cuenta bancaria",
  CONTRATO_FIRMADO: "Contrato firmado",
  INSCRIPCION_IVSS: "Inscripción IVSS",
  INSCRIPCION_FAOV: "Afiliación FAOV",
  NOTIFICACION_RIESGOS: "Notificación de riesgos",
}

export type PersonalLeido = {
  nombres: string | null
  apellidos: string | null
  tipo_documento: "V" | "E" | "P" | null
  documento: string | null
  fecha_nacimiento: string | null
  cedula_vence: string | null
  estado_civil: string | null
  genero: string | null
  nacionalidad: string | null
  rif: string | null
  rif_vence: string | null
  telefono: string | null
  email: string | null
  direccion: string | null
  profesion: string | null
  resumen_curricular: string | null
  estudios: Estudio[]
  experiencias: Experiencia[]
  confianza: "ALTA" | "MEDIA" | "BAJA"
  modelo_usado: string
}

export type LecturaPersonal = {
  lectura_id: string
  leido: PersonalLeido
  archivos: { nombre: string; tipo: TipoArchivo }[]
  se_puede_usar: boolean
  bloqueos: string[]
  avisos: string[]
  ficha_existente_id: number | null
}

/** Lo que «Usar estos datos» entrega a quien abrió el lector. */
export type Propuesta = {
  lectura_id: string
  campos: Record<string, string>
  estudios: Estudio[] | null
  experiencias: Experiencia[] | null
  archivos: LecturaPersonal["archivos"]
}

type CampoLeido = Exclude<
  keyof PersonalLeido,
  "estudios" | "experiencias" | "confianza" | "modelo_usado"
>

/** Campo leído → campo de la ficha, con su rótulo. En el orden en que se enseñan. */
export const CAMPOS: { leido: CampoLeido; ficha: keyof Persona; rotulo: string }[] = [
  { leido: "nombres", ficha: "nombres", rotulo: "Nombres" },
  { leido: "apellidos", ficha: "apellidos", rotulo: "Apellidos" },
  { leido: "tipo_documento", ficha: "tipo_documento", rotulo: "Tipo de documento" },
  { leido: "documento", ficha: "documento", rotulo: "Cédula" },
  { leido: "cedula_vence", ficha: "cedula_vence", rotulo: "Cédula vence" },
  { leido: "fecha_nacimiento", ficha: "fecha_nacimiento", rotulo: "Fecha de nacimiento" },
  { leido: "genero", ficha: "genero", rotulo: "Género" },
  { leido: "estado_civil", ficha: "estado_civil", rotulo: "Estado civil" },
  { leido: "nacionalidad", ficha: "nacionalidad", rotulo: "Nacionalidad" },
  { leido: "rif", ficha: "rif", rotulo: "RIF" },
  { leido: "rif_vence", ficha: "rif_vence", rotulo: "RIF vence" },
  { leido: "telefono", ficha: "telefono", rotulo: "Teléfono" },
  { leido: "email", ficha: "email_personal", rotulo: "Correo personal" },
  { leido: "direccion", ficha: "direccion", rotulo: "Dirección" },
  { leido: "profesion", ficha: "profesion", rotulo: "Profesión" },
  { leido: "resumen_curricular", ficha: "resumen_curricular", rotulo: "Resumen curricular" },
]

/** Los campos que se marcan solos: traen algo y no es lo que ya dice la ficha. */
export function marcadosDeEntrada(
  leido: PersonalLeido,
  actual: (campo: keyof Persona) => string,
): Set<string> {
  const marcados = new Set<string>()
  for (const c of CAMPOS) {
    const v = leido[c.leido]
    if (v && v !== actual(c.ficha)) marcados.add(c.ficha)
  }
  return marcados
}

/** Lo elegido, como campos de la ficha. Lo no marcado no viaja. */
export function propuestaDe(
  lectura: LecturaPersonal,
  marcados: Set<string>,
  conEstudios: boolean,
  conExperiencias: boolean,
): Propuesta {
  const campos: Record<string, string> = {}
  for (const c of CAMPOS) {
    const v = lectura.leido[c.leido]
    if (v && marcados.has(c.ficha)) campos[c.ficha] = v
  }
  return {
    lectura_id: lectura.lectura_id,
    campos,
    estudios: conEstudios ? lectura.leido.estudios : null,
    experiencias: conExperiencias ? lectura.leido.experiencias : null,
    archivos: lectura.archivos,
  }
}

/** Los formatos que Gemini lee. El portero del servidor admite más, pero no son legibles. */
export const LEGIBLES = ["application/pdf", "image/jpeg", "image/png", "image/webp"]
